import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {useInitialLayoutEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {UnreachableError} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {AdminSuggestionModel} from '../../../../../libs/shared/core/src/models/api/adminSuggestionsModel';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {ProductUpdate} from './productUpdatesHelpers';
import {SettingsAdminSuggestionsWidget} from './settingsAdminSuggestionsWidget';
import {SettingsHelpAndTipsWidget} from './settingsHelpAndTipsWidget';
import {SettingsHelpHeader, SettingsHelpHeaderTabsEnum} from './settingsHelpHeader';
import {SettingsProductUpdatesWidget} from './settingsProductUpdatesWidget';

/*
 * Props.
 */

interface SettingsHelpWidgetProps {
  panelId: AppPanelId;
  anchor: Anchor;
  onRequestClose: () => void;
  onOpenClick: () => void;
  adminSuggestions: ReadonlyArray<AdminSuggestionModel>;
  hasUnseenAdminSuggestions: boolean;
  onAdminSuggestionsSeen: () => void;
  productUpdates: ReadonlyArray<ProductUpdate> | null;
  hasUnseenProductUpdates: boolean;
  onProductUpdatesSeen: () => void;
}

const StyledWidgetDiv = styled.div`
  position: relative;
  bottom: 11px;
  width: 600px;
  max-height: 650px;
  overflow: hidden;

  background-color: ${(p) => p.theme.foreground};
  border-radius: 16px;
  ${shadows.dialogsAndDropdowns};
  bottom: 0;
  margin-top: 8px;
`;

/*
 * Component.
 */

export const SettingsHelpWidget: FC<SettingsHelpWidgetProps> = (props) => {
  const {
    onOpenClick,
    onRequestClose,
    adminSuggestions,
    hasUnseenAdminSuggestions,
    onAdminSuggestionsSeen,
    productUpdates,
    hasUnseenProductUpdates,
    onProductUpdatesSeen,
  } = props;

  const {trackDataEvent} = useDataEventTracking();

  const hasAdminSuggestionsTab = Boolean(adminSuggestions.length > 0);
  const hasProductUpdatesTab = Boolean(productUpdates && productUpdates.length > 0);

  const [lastSelectedTab, setLastSelectedTab] = useState(() => {
    if (hasProductUpdatesTab && hasUnseenProductUpdates) {
      return SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES;
    }

    if (hasAdminSuggestionsTab && hasUnseenAdminSuggestions) {
      return SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS;
    }

    return SettingsHelpHeaderTabsEnum.HELP;
  });

  // Fallback on the "Help" tab if the requested tab has disappeared.
  const selectedTab = useMemo(() => {
    switch (lastSelectedTab) {
      case SettingsHelpHeaderTabsEnum.HELP:
        return lastSelectedTab;

      case SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS:
        return hasAdminSuggestionsTab ? lastSelectedTab : SettingsHelpHeaderTabsEnum.HELP;

      case SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES:
        return hasProductUpdatesTab ? lastSelectedTab : SettingsHelpHeaderTabsEnum.HELP;

      default:
        throw new UnreachableError(lastSelectedTab);
    }
  }, [hasAdminSuggestionsTab, hasProductUpdatesTab, lastSelectedTab]);

  // Call `onOpenClick` on first layout effect.
  useInitialLayoutEffect(() => onOpenClick());

  const onClose = () => {
    trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.HELP_WIDGET_CLOSE);
    onRequestClose();
  };

  const trackSelectedTab = (tab: SettingsHelpHeaderTabsEnum) => {
    switch (tab) {
      case SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS:
        trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.HELP_WIDGET_ADMIN_SUGGESTIONS_TAB, {
          types: adminSuggestions.map((s) => s.type),
        });
        break;

      case SettingsHelpHeaderTabsEnum.HELP:
        trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.HELP_WIDGET_HELP_TAB);
        break;

      case SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES:
        trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.HELP_WIDGET_PRODUCT_UPDATES_TAB);
        break;

      default:
        throw new UnreachableError(tab);
    }
  };

  // NOTE - the SVGs below are the `closeSmall` and `helpFilledSelected` icons.
  // Due to a problem with the Chromium version of our desktop app, these SVGs
  // do not display properly when using the Icon component.
  // Therefore until we upgrade the Chromium version, we are embedding these SVGs directly.
  return (
    <RepositionPopover
      anchor={props.anchor}
      onRequestClose={onClose}
      placement="top-end"
      isInteractive
      isExclusive
    >
      <StyledWidgetDiv>
        <SettingsHelpHeader
          selectedTab={selectedTab}
          onSelectTab={(tab) => {
            trackSelectedTab(tab);
            setLastSelectedTab(tab);
          }}
          onRequestClose={onClose}
          hasAdminSuggestionsTab={hasAdminSuggestionsTab}
          hasUnseenAdminSuggestions={hasUnseenAdminSuggestions}
          hasProductUpdatesTab={hasProductUpdatesTab}
          hasUnseenProductUpdates={hasUnseenProductUpdates}
        />
        {renderContent(
          props,
          selectedTab,
          hasAdminSuggestionsTab,
          onAdminSuggestionsSeen,
          hasProductUpdatesTab,
          onProductUpdatesSeen,
        )}
      </StyledWidgetDiv>
    </RepositionPopover>
  );
};

function renderContent(
  props: SettingsHelpWidgetProps,
  selectedTab: SettingsHelpHeaderTabsEnum,
  hasAdminSuggestionsTab: boolean,
  onAdminSuggestionsTabSeen: () => void,
  hasProductUpdatesTab: boolean,
  onProductUpdatesTabSeen: () => void,
) {
  const {adminSuggestions, productUpdates, panelId} = props;

  if (selectedTab === SettingsHelpHeaderTabsEnum.PRODUCT_UPDATES && hasProductUpdatesTab) {
    return (
      <SettingsProductUpdatesWidget
        productUpdates={productUpdates ?? []}
        onContentSeen={onProductUpdatesTabSeen}
      />
    );
  }

  if (selectedTab === SettingsHelpHeaderTabsEnum.ADMIN_SUGGESTIONS && hasAdminSuggestionsTab) {
    return (
      <SettingsAdminSuggestionsWidget
        adminSuggestions={adminSuggestions}
        onRequestClose={props.onRequestClose}
        onTabSeen={onAdminSuggestionsTabSeen}
      />
    );
  }

  return <SettingsHelpAndTipsWidget panelId={panelId} />;
}
